import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { LandscapeResponseDto } from '../dto/response/landscape-response.dto';
import landscapeService from '../services/landspace.service';
import { LandscapeSaveRequestDto } from '../dto/request/landscape-save-request.dto';
import { LandscapeUpdateRequestDto } from '../dto/request/landscape-update-request.dto';
import { LandscapeSummaryResponseDto } from '../dto/response/landscape-summary-response.dto';
import { LandscapePagedListResponseDto } from '../dto/response/landscape-paged-list-response.dto';
import { showError, showSuccess } from '../../../../utils/Notifications';
import { AxiosError } from 'axios';
import { LocalesLanguagesConstants } from '../../../../locales/locales';

const initialState = {
  status: 'idle',
  landscapes: [] as LandscapeResponseDto[] | null,
  landscape: {} as LandscapeResponseDto | null,
  landscapeSummaries: [] as LandscapeSummaryResponseDto[] | null,
  landscapesPaged: {} as LandscapePagedListResponseDto | null,
  error: null as string | null,
};

export const fetchLandscapes = createAsyncThunk(
  'landscapes/fetchLandscapes',
  async (_, { rejectWithValue }) => {
    try {
      return await landscapeService.fetchLandscapes();
    } catch (e) {
      return rejectWithValue(
        (e as any).response?.data?.message?.join() ?? 'Failed'
      );
    }
  }
);

// export const fetchOrganizationLandscapes = createAsyncThunk(
//   'landscapes/fetchOrganizationLandscapes',
//   async (id: string, { rejectWithValue }) => {
//     try {
//       return await landscapeService.fetchOrganizationLandscapes(id);
//     } catch (e) {
//       return rejectWithValue(
//         (e as any).response?.data?.message?.join() ?? 'Failed'
//       );
//     }
//   }
// );

export const fetchLandscapeById = createAsyncThunk(
  'landscapes/fetchLandscapeById',
  async (params: { id: string; language: LocalesLanguagesConstants }) => {
    return await landscapeService.fetchLandscapeById(
      params.id,
      params.language
    );
  }
);

// export const fetchLandscapeSummaries = createAsyncThunk(
//   'landscapes/fetchLandscapeSummaries',
//   async () => {
//     return await landscapeService.fetchLandscapeSummaries();
//   }
// );

// export const fetchLandscapesPaged = createAsyncThunk(
//   'landscapes/fetchLandscapesPaged',
//   async (params: { pageIndex: number; pageSize: number }) => {
//     return await landscapeService.fetchLandscapesPaged(
//       params.pageIndex,
//       params.pageSize
//     );
//   }
// );

export const saveLandscape = createAsyncThunk(
  'landscapes/saveLandscape',
  async (params: {
    saveRequest: LandscapeSaveRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await landscapeService.saveLandscape(
      params.saveRequest,
      params.language
    );
  }
);

export const updateLandscape = createAsyncThunk(
  'landscapes/updateLandscape',
  async (params: {
    updateRequest: LandscapeUpdateRequestDto;
    language: LocalesLanguagesConstants;
  }) => {
    return await landscapeService.updateLandscape(
      params.updateRequest,
      params.language
    );
  }
);

export const deleteLandscape = createAsyncThunk(
  'landscapes/deleteLandscape',
  async (existingId: string, { rejectWithValue }) => {
    try {
      return await landscapeService.deleteLandscape(existingId);
    } catch (e) {
      return rejectWithValue((e as AxiosError).response?.data ?? 'Failed');
    }
  }
);

const landscapeSlice = createSlice({
  name: 'landscapeSlice',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLandscapeById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchLandscapeById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.landscape = action.payload;
      })
      .addCase(fetchLandscapeById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })

      .addCase(fetchLandscapes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchLandscapes.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.landscapes = action.payload;
      })
      .addCase(fetchLandscapes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      // .addCase(fetchOrganizationLandscapes.pending, (state) => {
      //   state.status = 'loading';
      // })
      // .addCase(fetchOrganizationLandscapes.fulfilled, (state, action) => {
      //   state.status = 'succeeded';
      //   state.landscapes = action.payload;
      // })
      // .addCase(fetchOrganizationLandscapes.rejected, (state, action) => {
      //   state.status = 'failed';
      //   state.error = action.payload as string;
      // })
      // .addCase(fetchLandscapeSummaries.pending, (state) => {
      //   state.status = 'loading';
      // })
      // .addCase(fetchLandscapeSummaries.fulfilled, (state, action) => {
      //   state.status = 'succeeded';
      //   state.landscapeSummaries = action.payload;
      // })
      // .addCase(fetchLandscapeSummaries.rejected, (state, action) => {
      //   state.status = 'failed';
      //   state.error = action.error?.message ?? null;
      // })
      // .addCase(fetchLandscapesPaged.pending, (state) => {
      //   state.status = 'loading';
      // })
      // .addCase(fetchLandscapesPaged.fulfilled, (state, action) => {
      //   state.status = 'succeeded';
      //   state.landscapesPaged = action.payload;
      // })
      // .addCase(fetchLandscapesPaged.rejected, (state, action) => {
      //   state.status = 'failed';
      //   state.error = action.error?.message ?? null;
      // })
      .addCase(saveLandscape.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(saveLandscape.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.landscapes?.push(action.payload);
        showSuccess('Landscape added successfully.');
      })
      .addCase(saveLandscape.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(updateLandscape.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateLandscape.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (state.landscapes) {
          const landscapeIndex = state.landscapes.findIndex(
            (landscape) => landscape.id === action.payload.id
          );
          if (landscapeIndex !== -1) {
            state.landscapes[landscapeIndex] = action.payload;
          }
        }
      })
      .addCase(updateLandscape.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error?.message ?? null;
      })
      .addCase(deleteLandscape.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteLandscape.fulfilled, (state) => {
        state.status = 'succeeded';
        showSuccess('Landscape deleted successfully.');
      })
      .addCase(deleteLandscape.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
        showError(`Deleting landscape failed. ${action.payload as string}`);
      });
  },
});

export default landscapeSlice.reducer;
